import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { ReactNode} from 'react';

import { ArrowLeft, Close, More, Share, TranslateBadge } from '@travel/icons/ui';
import { useDialogHandler, useOutsideClick, useTranslation } from '@travel/traveler-core/hooks';
import useDeviceType, { MOBILE_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import { Dialog } from '@travel/ui';
import type { GalleryMedia } from '@travel/ui/components/Gallery';
import { cx, isNotEmptyArray } from '@travel/utils';

import Media from 'components/Media';
import PhotoGalleryDetail from 'components/PhotoGalleryDetail';

import { getIsDirectContractType } from 'store/providerInformation/selectors';

import { GALLERY_INDEX_QUERY, GALLERY_QUERY, GALLERY_QUERY_VALUE } from 'constants/gallery';
import { Translate } from 'core/translate';
import useQueryState from 'hooks/useQueryState';
import useQueryToggle from 'hooks/useQueryToggle';

import {
  CATEGORY_ALL,
  CATEGORY_OVERVIEW,
  getFilterData,
  getGridAutoLayout,
  getImageType,
  removeGridType,
} from './helper';
import ShareMenuPopup from './ShareMenuPopup';

import styles from './photoGallery.module.scss';

type GridProps = {
  galleryData: GalleryMedia[];
  isGoogleTranslated: boolean;
  initImageIndex?: number;
};

function Grid({ galleryData, isGoogleTranslated, initImageIndex }: GridProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isDirectContractType = useSelector(getIsDirectContractType);
  const [selectedFilter, setSelectedFilter] = useState(
    isDirectContractType ? CATEGORY_ALL : CATEGORY_OVERVIEW,
  );
  const [imageIndex, setImageIndex] = useQueryState(GALLERY_INDEX_QUERY, {
    defaultValue: initImageIndex || 0,
    parser: parseInt,
  });
  const { isOpen, onOpen, onClose } = useQueryToggle(
    GALLERY_QUERY,
    GALLERY_QUERY_VALUE.ThumbnailDetail,
  );

  let filterList = getFilterData(galleryData);
  filterList = isDirectContractType ? filterList : filterList.filter(item => item !== 'All');
  const hasNoFilter = filterList.length === 1 && filterList[0] === CATEGORY_ALL;

  const filteredData =
    selectedFilter !== CATEGORY_ALL
      ? galleryData.filter(item => item.category === selectedFilter)
      : galleryData;

  const dataWithGridType = filteredData.map((item, index) => {
    const type = index === 0 ? 'large' : getImageType(item.size);
    return { ...item, gridType: type };
  });

  const autoLayoutData = useMemo(() => getGridAutoLayout(dataWithGridType), [dataWithGridType]);

  const handleClickFilter = (filter: string) => {
    setSelectedFilter(filter);
  };

  useEffect(() => {
    if (imageIndex) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 200);
    }
  }, [imageIndex]);

  return (
    <>
      {!hasNoFilter && (
        <div className={styles.gridFilter} data-testid="photoGallery-filter">
          {filterList.map((item, index) => (
            <button
              className={cx(styles.filterItem, selectedFilter === item ? styles.active : null)}
              onClick={() => handleClickFilter(item)}
              key={`${item}-${index}`}
              data-testid={`photoGallery-filter-${item}`}
            >
              {item === CATEGORY_ALL ? <Translate id="Traveler_Common.Common.All" /> : item}
            </button>
          ))}
        </div>
      )}
      <div
        className={cx(styles.gridWrapper, isGoogleTranslated && styles.gridWrapperShowIsTranslated)}
        data-testid="grid-wrapper"
      >
        <div className={styles.grid} data-testid="grid">
          {autoLayoutData.map((media, index) => {
            const handleOnClick = () => {
              onOpen();
              setImageIndex(index);
            };
            const shouldDisableLazyload = initImageIndex ? index <= initImageIndex + 4 : index <= 4;

            return (
              <div
                className={styles[media.gridType || '']}
                ref={index === imageIndex ? ref : null}
                key={`${media.name}-${index}`}
              >
                <Media
                  wrapperClassName={styles.mediaWrapper}
                  key={`${media.url}-${index}`}
                  media={removeGridType(media)}
                  sizes="100vw"
                  onClick={handleOnClick}
                  isDisableLazyload={shouldDisableLazyload}
                />
              </div>
            );
          })}
        </div>
        {isGoogleTranslated && (
          <div className={styles.translateBadge}>
            <TranslateBadge data-testid="translate-badge" />
          </div>
        )}
      </div>
      <PhotoGalleryDetail
        isOpen={isOpen}
        onClose={onClose}
        imageIndex={imageIndex}
        mediaData={autoLayoutData}
        onChangeIndex={setImageIndex}
      />
    </>
  );
}

export type PhotoGalleryProps = {
  galleryData: GalleryMedia[];
  isOpenPhotoGallery: boolean;
  onClosePhotoGallery: () => void;
  initImageIndex?: number;
  bookmarkButton?: ReactNode;
};

function PhotoGallery({
  galleryData,
  initImageIndex,
  isOpenPhotoGallery,
  bookmarkButton,
  onClosePhotoGallery,
}: PhotoGalleryProps) {
  const isSP = useDeviceType() === MOBILE_SCREEN;
  const isGoogleTranslated = React.useMemo(() => {
    return galleryData?.some(galleryMedia => galleryMedia.isTextsGoogleTranslated);
  }, [galleryData]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const shareMenuRef = useRef(null);
  const shareButtonRef = useRef(null);
  const { isOpen: isShareMenuOpen, onToggle: onToggleShareMenu } = useDialogHandler(false);

  const handleToggleShare = () => {
    if (!wrapperRef.current) {
      return;
    }

    if (isSP) {
      wrapperRef.current.style.overflow = !isShareMenuOpen ? 'hidden' : '';
    }

    onToggleShareMenu();
  };

  const shareTextLabel = useTranslation({ id: 'Provider_Info.Gallery.Share' });

  useOutsideClick(shareMenuRef, shareButtonRef, handleToggleShare);

  return isNotEmptyArray(galleryData) && isOpenPhotoGallery ? (
    <Dialog
      title={null}
      isOpen={isOpenPhotoGallery}
      onClose={onClosePhotoGallery}
      wrapperClassName={styles.gridDialog}
      className={styles.dialogWrapper}
      hasEnableBack={false}
      isUseContentFlexBox={false}
      contentClassName={styles.gridDialogContent}
    >
      <div ref={wrapperRef} className={styles.wrapper}>
        <div className={styles.actionBar}>
          <div className={styles.nav}>
            <button
              data-testid="back-button"
              type="button"
              className={styles.backButton}
              onClick={onClosePhotoGallery}
              aria-label="back button"
            >
              {isSP ? <Close size={24} /> : <ArrowLeft size={24} />}
            </button>
            <h5 className={styles.navTitle}>
              <Translate id="Provider_Info.Photos.Title" />
            </h5>
          </div>
          <div className={styles.actionItems} data-testid="photoGallery-actions">
            {!isSP && <div className={styles.bookmark}>{bookmarkButton}</div>}
            <button
              type="button"
              ref={shareButtonRef}
              className={styles.backButton}
              onClick={handleToggleShare}
              aria-label={shareTextLabel}
              data-testid="photoGallery-actions-share"
            >
              {isSP ? <More size={24} /> : <Share size={36} />}
            </button>
            <ShareMenuPopup
              isOpen={isShareMenuOpen}
              onClose={handleToggleShare}
              ref={shareMenuRef}
              bookmarkButton={isSP ? bookmarkButton : null}
            />
          </div>
        </div>
        <Grid
          galleryData={galleryData}
          initImageIndex={initImageIndex}
          isGoogleTranslated={isGoogleTranslated}
        />
      </div>
    </Dialog>
  ) : null;
}

export default PhotoGallery;
